import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useVenue } from "../../shell/VenueContext";
import type { TableResponse } from "../../features/floor-plan/floorApi";
import { layoutZones, TABLE_SIZE } from "../../features/floor-plan/floorLayout";
import { bookingClock, guestName, isNum, wallClockToEpochSec } from "../format";
import { overlaps, windowOf } from "../occupancy";
import { flattenPages, useBookingsList, useFloorPlan } from "../queries";
import type { BookingFilter } from "../searchParams";
import { banner, fieldLabel, textInput } from "../styles";

/**
 * Floor view: a read-only scene from the landed floor read model (§C.5), tables coloured by derived
 * occupancy at a chosen time; each table is a real, keyboard-focusable BUTTON opening its zone's
 * bookings (peek). A CAPACITY_ONLY venue has no drawn plan, so it degrades to zone lanes plus a
 * "set up your floor plan" CTA — never a blocked view (the capacity-only-first break decision).
 * Occupancy is zone-scoped (table assignment is not yet on the staff wire) — an honest degradation.
 */
export function FloorView({
  day,
  filter,
  onSelect,
}: {
  day: string;
  filter: BookingFilter;
  onSelect: (b: StaffBookingSummaryResponse) => void;
}) {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const plan = useFloorPlan(venueId);
  const dayFilter: BookingFilter = { ...filter, from: day, to: day };
  const query = useBookingsList(venueId, dayFilter);
  const rows = flattenPages(query.data);

  const [time, setTime] = useState("20:00");
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const atSec = wallClockToEpochSec(day, time, venue.timeZone);

  const zones = useMemo(() => plan.data?.zones ?? [], [plan.data]);
  const tables = useMemo(() => (plan.data?.tables ?? []).filter((tb) => !tb.retired), [plan.data]);

  // Bookings active at the chosen instant, grouped by their zone preference.
  const activeByZone = useMemo(() => {
    const at = windowOf(atSec, 1);
    const byZone = new Map<string, StaffBookingSummaryResponse[]>();
    for (const b of rows) {
      if (!isNum(b.startSec)) continue;
      if (!overlaps(at, windowOf(b.startSec, b.durationSec ?? 0))) continue;
      const pref = b.zonePreference?.trim().toLowerCase();
      const zone = zones.find((z) => z.id.toLowerCase() === pref || z.name.toLowerCase() === pref);
      const key = zone?.id ?? "__none__";
      const list = byZone.get(key) ?? [];
      list.push(b);
      byZone.set(key, list);
    }
    return byZone;
  }, [rows, zones, atSec]);

  if (plan.isLoading) {
    return (
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.list.loading")}</p>
    );
  }

  const capacityOnly = plan.data?.mode !== "FLOOR_PLAN" || tables.length === 0;

  const timeControl = (
    <label className={`${fieldLabel} ${css({ maxW: "40" })}`}>
      {t("bookings.floor.atTime")}
      <input
        type="time"
        className={textInput}
        value={time}
        onChange={(e) => setTime(e.target.value)}
      />
    </label>
  );

  if (capacityOnly) {
    return (
      <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
        <div role="note" className={banner("info")}>
          <span>{t("bookings.floor.capacityOnly")}</span>
          <Link
            to={`/v/${venueId}/floor-plan`}
            className={css({
              color: "colorPalette.text",
              textDecoration: "underline",
              fontWeight: "medium",
            })}
          >
            {t("bookings.floor.setup")}
          </Link>
        </div>
        {timeControl}
        <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          {zones.map((z) => {
            const active = activeByZone.get(z.id) ?? [];
            return (
              <li
                key={z.id}
                className={css({
                  borderWidth: "1px",
                  borderColor: "border.default",
                  rounded: "md",
                  p: "3",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2",
                })}
              >
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "medium",
                  })}
                >
                  <span>{z.name}</span>
                  <span className={css({ fontSize: "sm", color: "fg.muted" })}>
                    {t("bookings.floor.seatedNow", { count: active.length })}
                  </span>
                </div>
                <ZoneBookings
                  items={active}
                  onSelect={onSelect}
                  tz={venue.timeZone}
                  locale={i18n.language}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // Lay each zone out in its own horizontal band, stacked top-to-bottom with a per-zone y-offset, so
  // two zones whose seeded grids share a coordinate range no longer interleave on the canvas. Within a
  // band the tables keep their relative geometry (the manager's arrangement); only the whole zone is
  // shifted. Each band carries a label so the canvas reads as named rooms, not a soup of squares.
  const layout = layoutZones(zones, tables);

  const zoneName = (id: string | null) => (id ? (zones.find((z) => z.id === id)?.name ?? id) : "");
  const selectedBookings = selectedZone ? (activeByZone.get(selectedZone) ?? []) : [];

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      {timeControl}
      <div
        aria-label={t("bookings.floor.plan")}
        className={css({
          position: "relative",
          width: "full",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "lg",
          bg: "bg.subtle",
        })}
        style={{ aspectRatio: `${layout.width} / ${layout.height}`, maxHeight: "70vh" }}
      >
        {layout.bands.map((band) => (
          <div
            key={band.id}
            aria-hidden="true"
            className={css({
              position: "absolute",
              rounded: "md",
              borderWidth: "1px",
              borderStyle: "dashed",
              borderColor: "border.default",
              bg: "bg.muted/40",
            })}
            style={{
              left: `${(band.left / layout.width) * 100}%`,
              top: `${(band.top / layout.height) * 100}%`,
              width: `${(band.width / layout.width) * 100}%`,
              height: `${(band.height / layout.height) * 100}%`,
            }}
          >
            <span
              className={css({
                position: "absolute",
                top: "1",
                left: "2",
                fontSize: "2xs",
                fontWeight: "semibold",
                letterSpacing: "wide",
                textTransform: "uppercase",
                color: "fg.muted",
              })}
            >
              {band.name}
              {(() => {
                const n = activeByZone.get(band.id)?.length ?? 0;
                return n > 0 ? ` · ${t("bookings.floor.seatedNow", { count: n })}` : "";
              })()}
            </span>
          </div>
        ))}
        {layout.placed.map(({ tb, x, y }) => {
          // Peek view: occupancy here is only known at the ZONE level (the staff list is not table-keyed),
          // so tables render as neutral room elements and the seated count lives on the zone band above —
          // never a per-table colour, which would falsely imply a specific table is taken. The live-service
          // floor (`/v/:id/live`) is the operational surface with true per-table state.
          const zoneSeated = activeByZone.get(tb.zoneId)?.length ?? 0;
          return (
            <TableButton
              key={tb.id}
              table={tb}
              left={(x / layout.width) * 100}
              top={(y / layout.height) * 100}
              wPct={(TABLE_SIZE / layout.width) * 100}
              hPct={(TABLE_SIZE / layout.height) * 100}
              label={t("bookings.floor.tableZoneLabel", {
                label: tb.label,
                zone: zones.find((z) => z.id === tb.zoneId)?.name ?? "",
                count: zoneSeated,
              })}
              onSelect={() => setSelectedZone(tb.zoneId)}
            />
          );
        })}
      </div>

      {selectedZone ? (
        <div
          className={css({
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
            p: "3",
            display: "flex",
            flexDirection: "column",
            gap: "2",
          })}
        >
          <h3 className={css({ fontSize: "sm", fontWeight: "semibold" })}>
            {t("bookings.floor.zoneAt", { zone: zoneName(selectedZone), time })}
          </h3>
          <ZoneBookings
            items={selectedBookings}
            onSelect={onSelect}
            tz={venue.timeZone}
            locale={i18n.language}
          />
        </div>
      ) : null}
    </div>
  );
}

function TableButton({
  table,
  left,
  top,
  wPct,
  hPct,
  label,
  onSelect,
}: {
  table: TableResponse;
  left: number;
  top: number;
  wPct: number;
  hPct: number;
  label: string;
  onSelect: () => void;
}) {
  const round = table.shape === "ROUND" || table.shape === "OVAL";
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onSelect}
      className={`${neutralTable} ${css({
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "2xs",
        fontWeight: "medium",
        borderWidth: "2px",
        _focusVisible: {
          outlineWidth: "2px",
          outlineColor: "colorPalette.default",
          outlineOffset: "1px",
        },
      })}`}
      style={{
        left: `${left}%`,
        top: `${top}%`,
        width: `${wPct}%`,
        height: `${hPct}%`,
        borderRadius: round ? "9999px" : "6px",
      }}
    >
      {table.label}
    </button>
  );
}

function ZoneBookings({
  items,
  onSelect,
  tz,
  locale,
}: {
  items: StaffBookingSummaryResponse[];
  onSelect: (b: StaffBookingSummaryResponse) => void;
  tz: string;
  locale: string;
}) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.floor.noneHere")}</p>
    );
  }
  return (
    <ul className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
      {items.map((b) => (
        <li key={b.bookingId}>
          <button
            type="button"
            className={css({
              fontSize: "sm",
              color: "colorPalette.text",
              textDecoration: "underline",
            })}
            onClick={() => onSelect(b)}
          >
            {isNum(b.startSec) ? bookingClock(b.startSec, tz, locale) : ""} ·{" "}
            {guestName(b, t("bookings.noName"))} ({b.partySize ?? "?"})
          </button>
        </li>
      ))}
    </ul>
  );
}

const neutralTable = css({ bg: "bg.default", color: "fg.default", borderColor: "border.default" });
